import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Select from "react-select";
import axios from "../../api/axios";
import { useToken } from "../../Hooks/useToken";
import styles from "./CreateAccount.module.css";
import { TextField, Typography, Checkbox, FormControlLabel, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterLuxonSunday } from "../../utils/AdapterSundayLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers";

// Removed unused loginUser function

export default function CreateAccount() {
  const [username, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState(null);
  const [verifypassword, setVerifyPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [dob, setDOB] = useState(null);
  const [newsletterSubscribe, setNewsletterSubscribe] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [token, setToken] = useToken();
  const navigate = useNavigate();

  // Redirect if user is already logged in
  useEffect(() => {
    if (token) {
      navigate('/events', { replace: true });
    }
  }, [token, navigate]);

  const gendersList = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password strength validation
  const validatePassword = (password) => {
    // Reset password strength if password is empty
    if (!password || password.length === 0) {
      setPasswordStrength({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      });
      return false;
    }
    
    const strength = {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordStrength(strength);
    return Object.values(strength).every(Boolean);
  };

  // Real-time validation functions
  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'email':
        if (!value) {
          newErrors.email = 'Email is required';
        } else if (!validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      case 'fullName':
        if (!value) {
          newErrors.fullName = 'Full Name is required';
        } else if (value.length < 2) {
          newErrors.fullName = 'Full Name must be at least 2 characters';
        } else {
          delete newErrors.fullName;
        }
        break;
      case 'dob':
        if (!value) {
          newErrors.dob = 'Date of Birth is required';
        } else {
          delete newErrors.dob;
        }
        break;
      case 'gender':
        if (!value) {
          newErrors.gender = 'Gender is required';
        } else {
          delete newErrors.gender;
        }
        break;
      case 'password':
        if (!value) {
          newErrors.password = 'Password is required';
        } else if (!validatePassword(value)) {
          newErrors.password = 'Password must meet all requirements';
        } else {
          delete newErrors.password;
        }
        break;
      case 'confirmPassword':
        if (!value) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (value !== password) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  // Helper function to check password validity without updating strength state
  const checkPasswordValidity = (password) => {
    if (!password || password.length === 0) return false;
    
    const strength = {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return Object.values(strength).every(Boolean);
  };

  // Check form validity whenever any field changes
  useEffect(() => {
    const hasEmailError = !username || !validateEmail(username);
    const hasFullNameError = !fullName || fullName.length < 2;
    const hasDobError = !dob;
    const hasGenderError = !gender;
    const hasPasswordError = !password || !checkPasswordValidity(password);
    const hasConfirmPasswordError = !verifypassword || verifypassword !== password;
    
    const isValid = !hasEmailError && !hasFullNameError && !hasDobError && 
                   !hasGenderError && !hasPasswordError && !hasConfirmPasswordError;
    
    setIsFormValid(isValid);
  }, [username, fullName, dob, gender, password, verifypassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      enqueueSnackbar("Please fill out all required fields correctly.", { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    setShowSuccessMessage(false);
    
    try {
      // First create account
      await CreateAccount(username, password, fullName, dob, newsletterSubscribe, gender);
      
      // Show success message
      setShowSuccessMessage(true);
      enqueueSnackbar("Your account has been created successfully. Please check your email to verify.", { 
        variant: 'success',
        autoHideDuration: 6000
      });
      
      // Clear form
      setUserName('');
      setPassword('');
      setFullName('');
      setGender(null);
      setVerifyPassword('');
      setDOB(null);
      setNewsletterSubscribe(false);
      setErrors({});
      setIsFormValid(false);
      setShowPasswordRequirements(false);
      setPasswordStrength({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      });
      
      // Redirect after a delay
      //setTimeout(() => {
      //  navigate("/login");
      //}, 3000);
      
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 409) {
        enqueueSnackbar("This email address is already registered. Please use a different email or try logging in.", { 
          variant: 'error',
          autoHideDuration: 8000
        });
        // Highlight email field
        setErrors({...errors, email: 'This email is already registered'});
      } else if (error.response?.status === 400) {
        enqueueSnackbar("Please check your information and try again. Some fields may be invalid.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else if (error.response?.status >= 500) {
        enqueueSnackbar("Server error occurred. Please try again later or contact support.", { 
          variant: 'error',
          autoHideDuration: 8000
        });
      } else if (!navigator.onLine) {
        enqueueSnackbar("No internet connection. Please check your connection and try again.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else {
        enqueueSnackbar("Account creation failed. Please try again or contact support if the problem persists.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  async function CreateAccount(username, password, fullName, dob, newsletterSubscribe, genderValue) {
    const userData = {
      UserName: username,
      UserPassword: password,
      FirstName: fullName.split(' ')[0] || '',
      LastName: fullName.split(' ').slice(1).join(' ') || '',
      FullName: fullName,
      Email: username, // Email is the username
      DateOfBirth: dob.toFormat('D'),
      BP_SubscribeNews: newsletterSubscribe,
      UserGender: genderValue ? genderValue.value : null,
      // Required fields for backend
      UserEnabled: true,
      UserIsEditor: false,
      UserIsGlobalAdministrator: false,
      UserLastModified: new Date().toISOString()
    };
    
    // Backend expects data wrapped in 'data' property
    const response = await axios.post("/api/users", JSON.stringify({ data: userData }), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  return (
    <>
      <div className={styles.accountNav}>
        <Typography className={styles.navText}>Sharing God's Love in Word and Deed</Typography>
      </div>
      <div className={styles.CreateAccountMain}>
        <div id="app" className={styles.contentHalf}>
          <section className={styles.loginFormSection}>
            <img
              src="./Images/baptists-on-mission-logo.png"
              alt="Baptists on Mission Logo"
              className={styles.bomLogo}
            />
            {showSuccessMessage ? (
              // Success message - hide the form and show only this
              <div className={styles.successContainer}>
                <div className={styles.successMessage}>
                  <Typography variant="h4" className={styles.successTitle}>
                    Account Created Successfully!
                  </Typography>
                  <Typography variant="body2" className={styles.successText}>
                    Your account has been created successfully. Please check your email to verify your account.
                  </Typography>
                  
                </div>
              </div>
            ) : (
              // Show the form when success message is not displayed
              <form onSubmit={handleSubmit} className={styles.loginForm}>
                <div className={styles.formGroup}>
                <Typography variant="formLabel" className={styles.requiredLabel}>
                  Full Name 
                  </Typography>
                  <TextField
                    type="text"
                  placeholder="Enter your full name"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    validateField('fullName', e.target.value);
                  }}
                  error={!!errors.fullName}
                  fullWidth
                />
                {errors.fullName && (
                  <Typography variant="caption" color="error" className={styles.errorText}>
                    {errors.fullName}
                  </Typography>
                )}
              </div>
              <div className={styles.formGroup}>
                <Typography variant="formLabel" className={`${styles.requiredLabel} ${styles.emailLabelContainer}`}>
                  Email Address &nbsp;
                  <small id="emailHelp" className={`form-text text-muted ${styles.emailHelper}`}>
                    This will be your username
                  </small>
                </Typography>
                
                <TextField
                  type="email"
                  id="email"
                  placeholder="Enter your email address"
                  value={username}
                  onChange={(e) => {
                    setUserName(e.target.value);
                    validateField('email', e.target.value);
                  }}
                  error={!!errors.email}
                  fullWidth
                /> 
              
                {errors.email && (
                  <Typography variant="caption" color="error" className={styles.errorText}>
                    {errors.email}
                  </Typography>
                )}
                
              </div>
              <div className={styles.formRow}>
              <div className={styles.formGroup}>
                  <Typography variant="formLabel" className={styles.requiredLabel}>
                  Date of Birth
                </Typography>
                <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
                  
<DatePicker
    value={dob}
    onChange={(newValue) => {
        setDOB(newValue);
        validateField('dob', newValue);
    }}
    slotProps={{
        textField: {
            error: !!errors.dob,
            fullWidth: true,
        },
    }}
/>
                </LocalizationProvider>
                  {errors.dob && (
                    <Typography variant="caption" color="error" className={styles.errorText}>
                      {errors.dob}
                    </Typography>
                  )}
              </div>
              <div className={styles.formGroup}>
                  <Typography variant="formLabel" className={styles.requiredLabel}>
                  Gender
                </Typography>
                <Select
                  name="genderselect"
                  options={gendersList}
                  id="gender"
                  value={gender}
                    onChange={(selectedOption) => {
                      setGender(selectedOption);
                      validateField('gender', selectedOption);
                    }}
                  className={styles.select}
                    placeholder="Select gender"
                    isSearchable={false}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '48.5px',
                        height: '48.5px',
                        borderColor: state.isFocused ? '#1976d2' : errors.gender ? '#d32f2f' : 'rgba(0, 0, 0, 0.15)',
                        borderWidth: state.isFocused ? '2px' : '1px',
                        borderRadius: '0.375rem',
                        boxShadow: state.isFocused ? '0 0 0 1px #1976d2' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#1976d2' : errors.gender ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)'
                        }
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        height: '48.5px',
                        padding: '0 14px',
                        display: 'flex',
                        alignItems: 'center'
                      }),
                      input: (provided) => ({
                        ...provided,
                        margin: '0',
                        padding: '0',
                        height: 'auto'
                      }),
                      indicatorSeparator: () => ({
                        display: 'none'
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: '8px'
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: '14px'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        fontSize: '16px',
                        color: 'rgba(0, 0, 0, 0.87)'
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                  />
                  {errors.gender && (
                    <Typography variant="caption" color="error" className={styles.errorText}>
                      {errors.gender}
                    </Typography>
                  )}
                </div>
              </div>
              <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <div className={styles.passwordFieldWrapper}>
                  <Typography variant="formLabel" className={styles.requiredLabel}>
                    Password
                  </Typography>
                  <TextField
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setPassword(newPassword);
                      validatePassword(newPassword);
                      validateField('password', newPassword);
                    }}
                    onFocus={() => setShowPasswordRequirements(true)}
                    onBlur={() => setShowPasswordRequirements(false)}
                    error={!!errors.password}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors.password && (
                    <Typography variant="caption" color="error" className={styles.errorText}>
                      {errors.password}
                    </Typography>
                  )}
                  
                  {/* Password Requirements Tooltip */}
                  {showPasswordRequirements && (
                    <>
                      <div className={styles.tooltipBackdrop} onClick={() => setShowPasswordRequirements(false)} />
                      <div className={styles.passwordRequirements}>
                        
                      <div className={styles.requirementsList}>
                        <div className={`${styles.requirementItem} ${passwordStrength.length ? styles.met : ''}`}>
                          <span className={styles.checkIcon}>
                            {passwordStrength.length ? (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#4caf50"/>
                                <path d="M6 9L8 11L12 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#e0e0e0"/>
                              </svg>
                            )}
                          </span>
                          <span className={styles.requirementText}>At least 12 characters</span>
                        </div>
                        <div className={`${styles.requirementItem} ${passwordStrength.uppercase ? styles.met : ''}`}>
                          <span className={styles.checkIcon}>
                            {passwordStrength.uppercase ? (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#4caf50"/>
                                <path d="M6 9L8 11L12 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#e0e0e0"/>
                              </svg>
                            )}
                          </span>
                          <span className={styles.requirementText}>One uppercase letter (A-Z)</span>
                        </div>
                        <div className={`${styles.requirementItem} ${passwordStrength.lowercase ? styles.met : ''}`}>
                          <span className={styles.checkIcon}>
                            {passwordStrength.lowercase ? (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#4caf50"/>
                                <path d="M6 9L8 11L12 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#e0e0e0"/>
                              </svg>
                            )}
                          </span>
                          <span className={styles.requirementText}>One lowercase letter (a-z)</span>
                        </div>
                        <div className={`${styles.requirementItem} ${passwordStrength.number ? styles.met : ''}`}>
                          <span className={styles.checkIcon}>
                            {passwordStrength.number ? (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#4caf50"/>
                                <path d="M6 9L8 11L12 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#e0e0e0"/>
                              </svg>
                            )}
                          </span>
                          <span className={styles.requirementText}>One number (0-9)</span>
                        </div>
                        <div className={`${styles.requirementItem} ${passwordStrength.special ? styles.met : ''}`}>
                          <span className={styles.checkIcon}>
                            {passwordStrength.special ? (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#4caf50"/>
                                <path d="M6 9L8 11L12 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <circle cx="9" cy="9" r="9" fill="#e0e0e0"/>
                              </svg>
                            )}
                          </span>
                          <span className={styles.requirementText}>One special character (!@#$%...)</span>
                        </div>
                      </div>
                     
                    </div>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.formGroup}>
                  <Typography variant="formLabel" className={styles.requiredLabel}>
                    Confirm Password
                  </Typography>
                <TextField
                  type={showVerifyPassword ? 'text' : 'password'}
                  className={styles.confirmPasswordField}
                  id="verifypassword"
                  placeholder="Confirm your password"
                  value={verifypassword}
                    onChange={(e) => {
                      setVerifyPassword(e.target.value);
                      validateField('confirmPassword', e.target.value);
                    }}
                    error={!!errors.confirmPassword}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showVerifyPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowVerifyPassword((s) => !s)}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showVerifyPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors.confirmPassword && (
                    <Typography variant="caption" color="error" className={styles.errorText}>
                      {errors.confirmPassword}
                    </Typography>
                  )}
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newsletterSubscribe}
                      onChange={(e) => setNewsletterSubscribe(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Stay up to date on events and opportunities"
                  className={styles.newsletterCheckbox}
                />
              </div>
              <button
                type="submit"
                className="button-default button-full-width"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
            )}
            
            {!showSuccessMessage && (
              <>
                <p className="problem-text">
                  If you have any problems creating an account, please contact us at
                  (800) 395-5102 ext 5596
                </p>
                <p className={styles.signInLink}>
                  Already have an account? <NavLink to="/login">Sign In</NavLink>
                </p>
              </>
            )}
          </section>
        </div>
        <img
          className={styles.createAccountImg}
          src="./Images/create-account-page-image.jpg"
          alt="Baptists on Mission create account"
        />
      </div>
    </>
  );
}
